/**
 * palette-routes — static registry of typeable navigation + creation entries
 * shown in the global ⌘K command palette (Phase 12.3).
 *
 * Hand-curated rather than auto-discovered from the TanStack Router tree
 * because labels need to be Czech and human-readable ("Plánované kontakty",
 * not `/kontakty/planovane`). `keywords` carry English / latin aliases so a
 * user can type "vehicles" → Vozidla, "customers" → Zákazníci, etc.
 *
 * The palette filters entries with a simple case-insensitive substring match
 * against `label + keywords`. cmdk's own fuzzy filter stays disabled
 * (`shouldFilter={false}`) so the API-result groups (Kontakty / Zákazníci /
 * Vozidla — already filtered server-side) coexist with the routes group.
 */

import type { Modul, NeedUroven } from '@/lib/permissions';

export type PaletteRouteGroup = 'Navigace' | 'Vytvořit' | 'Správa';

export interface PaletteRoute {
  /** Stable id (used as cmdk's `value` to keep keyboard nav stable). */
  id: string;
  /** Display label, Czech. */
  label: string;
  /** Optional second line (e.g. parent module). */
  sub?: string;
  /** Section heading in the palette. */
  group: PaletteRouteGroup;
  /** Pathname (with optional query string) to navigate to on select. */
  to: string;
  /** Aliases used for fuzzy matching alongside the label. */
  keywords?: string[];
  /**
   * Extra level requirement beyond the path's module visibility — e.g. the
   * module admin tools under `/vozidla/sprava` need `can('vozidla','admin')`,
   * not just vozidla being visible. Checked once membership resolves.
   */
  gate?: { modul: Modul; need: NeedUroven };
}

export const PALETTE_ROUTES: readonly PaletteRoute[] = [
  // ── Navigace ────────────────────────────────────────────────────────────
  {
    id: 'nav.dashboard',
    label: 'Přehled',
    sub: 'Hlavní stránka',
    group: 'Navigace',
    to: '/',
    keywords: ['dashboard', 'domu', 'home', 'prehled', 'uvod'],
  },
  {
    id: 'nav.kontakty',
    label: 'Kontakty',
    group: 'Navigace',
    to: '/kontakty',
    keywords: ['contacts', 'leady', 'leads'],
  },
  {
    id: 'nav.kontakty-planovane',
    label: 'Plánované kontakty',
    sub: 'Kontakty',
    group: 'Navigace',
    to: '/kontakty/planovane',
    keywords: ['planovane', 'plany', 'todo', 'pripravne'],
  },
  {
    id: 'nav.vozidla',
    label: 'Vozidla',
    group: 'Navigace',
    to: '/vozidla',
    keywords: ['vehicles', 'auta', 'nova', 'sklad'],
  },
  {
    id: 'nav.vozidla-nabidky',
    label: 'Nabídky',
    sub: 'Vozidla',
    group: 'Navigace',
    to: '/vozidla/nabidky',
    keywords: ['offers', 'nabidky', 'cenove'],
  },
  {
    id: 'nav.vozidla-smlouvy',
    label: 'Smlouvy',
    sub: 'Vozidla',
    group: 'Navigace',
    to: '/vozidla/smlouvy',
    keywords: ['contracts', 'kupni', 'prodej'],
  },
  {
    id: 'nav.vozidla-upravena',
    label: 'Upravená vozidla',
    sub: 'Vozidla',
    group: 'Navigace',
    to: '/vozidla/upravena',
    keywords: ['custom', 'tuning'],
  },
  {
    id: 'nav.vozidla-vyhodnoceni',
    label: 'Vyhodnocení smluv',
    sub: 'Vozidla',
    group: 'Navigace',
    to: '/vozidla/vyhodnoceni',
    keywords: ['reports', 'analytics', 'vykazy', 'statistiky', 'grafy'],
  },
  {
    id: 'nav.vozidla-sprava-ceniky',
    label: 'Ceníky',
    sub: 'Vozidla · Správa',
    group: 'Navigace',
    to: '/vozidla/sprava?tab=ceniky',
    keywords: ['pricing', 'pricelist', 'sprava'],
    gate: { modul: 'vozidla', need: 'admin' },
  },
  {
    id: 'nav.vozidla-sprava-lms',
    label: 'LMS katalog',
    sub: 'Vozidla · Správa',
    group: 'Navigace',
    to: '/vozidla/sprava?tab=lms',
    keywords: ['lms', 'catalog', 'sprava'],
    gate: { modul: 'vozidla', need: 'admin' },
  },
  {
    id: 'nav.vozidla-sprava-lms-sync',
    label: 'LMS synchronizace',
    sub: 'Vozidla · Správa',
    group: 'Navigace',
    to: '/vozidla/sprava?tab=lms-sync',
    keywords: ['lms', 'sync', 'sklad', 'sprava'],
    gate: { modul: 'vozidla', need: 'admin' },
  },
  {
    id: 'nav.ojeta',
    label: 'Ojetá vozidla',
    group: 'Navigace',
    to: '/ojeta-vozidla',
    keywords: ['used', 'second-hand', 'bazar', 'ojetiny'],
  },
  {
    id: 'nav.ojeta-nabidky',
    label: 'Nabídky',
    sub: 'Ojetá vozidla',
    group: 'Navigace',
    to: '/ojeta-vozidla/nabidky',
    keywords: ['used offers'],
  },
  {
    id: 'nav.ojeta-smlouvy',
    label: 'Smlouvy',
    sub: 'Ojetá vozidla',
    group: 'Navigace',
    to: '/ojeta-vozidla/smlouvy',
    keywords: ['used contracts'],
  },
  {
    id: 'nav.ojeta-vykup',
    label: 'Výkup',
    sub: 'Ojetá vozidla',
    group: 'Navigace',
    to: '/ojeta-vozidla/vykup',
    keywords: ['protiucet', 'tradein', 'buyback'],
  },
  {
    id: 'nav.leasing',
    label: 'Leasing',
    group: 'Navigace',
    to: '/leasing',
    keywords: ['financovani', 'lease'],
  },
  {
    id: 'nav.zakaznici',
    label: 'Zákazníci',
    group: 'Navigace',
    to: '/zakaznici',
    keywords: ['customers', 'klienti', 'firmy'],
  },
  {
    id: 'nav.zakaznici-sprava-dedup',
    label: 'Deduplikace zákazníků',
    sub: 'Zákazníci · Správa',
    group: 'Navigace',
    to: '/zakaznici/sprava',
    keywords: ['merge', 'duplicates', 'duplikaty', 'slouceni', 'sprava'],
    gate: { modul: 'zakaznici', need: 'admin' },
  },
  {
    id: 'nav.servis-planovani',
    label: 'Plánování',
    sub: 'Servis',
    group: 'Navigace',
    to: '/servis/planovani',
    keywords: ['service', 'servis', 'kalendar', 'rezervace'],
  },
  {
    id: 'nav.servis-vyhledani',
    label: 'Vyhledání',
    sub: 'Servis',
    group: 'Navigace',
    to: '/servis/vyhledani',
    keywords: ['search', 'najit', 'service search'],
  },
  {
    id: 'nav.servis-historie',
    label: 'Historie',
    sub: 'Servis',
    group: 'Navigace',
    to: '/servis/historie',
    keywords: ['history', 'archiv'],
  },
  {
    id: 'nav.servis-uskladneni',
    label: 'Uskladnění',
    sub: 'Servis',
    group: 'Navigace',
    to: '/servis/uskladneni',
    keywords: ['storage', 'pneu', 'tires', 'kola'],
  },
  {
    id: 'nav.posta',
    label: 'Pošta',
    group: 'Navigace',
    to: '/posta',
    keywords: ['mail', 'zasilky', 'post', 'odeslana', 'prijata'],
  },
  {
    id: 'nav.kalendar',
    label: 'Kalendář',
    group: 'Navigace',
    to: '/kalendar',
    keywords: ['calendar', 'predani', 'terminy', 'stk'],
  },
  {
    id: 'nav.pujcovna',
    label: 'Půjčovna',
    group: 'Navigace',
    to: '/pujcovna',
    keywords: ['rental', 'pujcovna', 'pobocky', 'pronajem', 'rezervace', 'vozovy park'],
  },

  // ── Vytvořit ────────────────────────────────────────────────────────────
  {
    id: 'create.zakaznik',
    label: 'Nový zákazník',
    sub: 'Zákazníci',
    group: 'Vytvořit',
    to: '/zakaznici/novy',
    keywords: ['add customer', 'new customer', 'novy klient'],
  },
  {
    id: 'create.kontakt',
    label: 'Nový kontakt',
    sub: 'Kontakty',
    group: 'Vytvořit',
    to: '/kontakty/novy',
    keywords: ['add contact', 'new lead', 'novy lead'],
  },
  {
    id: 'create.zasilka',
    label: 'Nová zásilka',
    sub: 'Pošta',
    group: 'Vytvořit',
    to: '/posta/novy',
    keywords: ['add shipment', 'new shipment', 'odeslat'],
  },

  // ── Správa firmy (company admin) + Platforma (platform admin) ─────────────
  {
    id: 'mgmt.overview',
    label: 'Správa firmy',
    sub: 'Přehled',
    group: 'Správa',
    to: '/management',
    keywords: ['management', 'admin', 'spravce'],
  },
  {
    id: 'mgmt.clenove',
    label: 'Členové',
    sub: 'Správa firmy',
    group: 'Správa',
    to: '/management?tab=clenove',
    keywords: ['members', 'prava', 'permissions'],
  },
  {
    id: 'plat.firmy',
    label: 'Firmy',
    sub: 'Platforma',
    group: 'Správa',
    to: '/platforma',
    keywords: ['companies', 'dealerships', 'tenants', 'lms'],
  },
  {
    id: 'plat.uzivatele',
    label: 'Uživatelé',
    sub: 'Platforma',
    group: 'Správa',
    to: '/platforma?tab=uzivatele',
    keywords: ['users', 'accounts', 'lms'],
  },
  {
    id: 'plat.ulohy',
    label: 'Záznamy úloh',
    sub: 'Platforma',
    group: 'Správa',
    to: '/platforma?tab=ulohy',
    keywords: ['cron', 'jobs', 'logs'],
  },
  {
    id: 'plat.diagnostika',
    label: 'Diagnostika',
    sub: 'Platforma',
    group: 'Správa',
    to: '/platforma?tab=diagnostika',
    keywords: ['proxy', 'traefik', 'ip', 'forwarded', 'headers', 'síť'],
  },
] as const;

/**
 * Diacritic-folded, lowercased haystack for cheap substring matching.
 * `"Plánované"` → `"planovane"`. Both the user query and each route's
 * (label + keywords + sub) get folded so "planovane" matches "Plánované"
 * without forcing the user to type Czech diacritics.
 */
function fold(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Filter routes by a free-text query. Empty query → return all routes
 * unchanged. Substring is matched against the folded `label + sub + keywords`
 * haystack so common Czech / English aliases work without typing diacritics.
 */
export function filterPaletteRoutes(
  routes: readonly PaletteRoute[],
  query: string,
): readonly PaletteRoute[] {
  const q = fold(query.trim());
  if (!q) return routes;
  return routes.filter((r) => {
    const haystack = [r.label, r.sub ?? '', ...(r.keywords ?? [])]
      .map(fold)
      .join(' ');
    // Tokenized AND-match: every space-separated token must appear.
    return q.split(/\s+/).every((tok) => haystack.includes(tok));
  });
}

/**
 * Group routes by their `group` field, preserving the registry's insertion
 * order within each section.
 */
export function groupPaletteRoutes(
  routes: readonly PaletteRoute[],
): { group: PaletteRouteGroup; routes: PaletteRoute[] }[] {
  const order: PaletteRouteGroup[] = ['Navigace', 'Vytvořit', 'Správa'];
  const buckets = new Map<PaletteRouteGroup, PaletteRoute[]>();
  for (const g of order) buckets.set(g, []);
  for (const r of routes) {
    const bucket = buckets.get(r.group);
    if (bucket) bucket.push(r);
  }
  return order
    .map((g) => ({ group: g, routes: buckets.get(g) ?? [] }))
    .filter((b) => b.routes.length > 0);
}
